import { windOffsetPx } from '../environment/wind.ts';
import { contactShape, DEFAULT_HULL, type HullDimensions } from '../hullSize.ts';
import { boatNudgeClosingPx } from '../modules/host.ts';
import type { MapDocument } from '../map/types.ts';
import {
  isValidDestination,
  projectMovementArea,
  resolveMove,
  solveTurnArc,
} from '../movement.ts';
import { projectMovePath } from '../path.ts';
import { advanceRace, finishProgress, type RaceState } from '../race.ts';
import type { FleetContext } from '../turn.ts';
import { seatCommitArgs, type TurnFeel } from '../turnFeel.ts';
import type { BoatState, Vec2 } from '../types.ts';
import { rasterSolidAt, type Raster } from './raster.ts';

/**
 * Another boat as its contact capsule (the spine's two ends and the half beam) and its way: the
 * velocity of its last move, which is what a hit's severity is measured against.
 */
export type FleetHull = { from: Vec2; to: Vec2; radius: number; vx: number; vy: number };

type Box = { minX: number; minY: number; maxX: number; maxY: number };

/** Everything a step needs that does not change during one search. */
export type StepContext = {
  map: MapDocument;
  feel: TurnFeel;
  raster: Raster;
  /** Every gate's box, grown a little, so a path nowhere near a gate skips the scorer. */
  gateBoxes: readonly Box[];
  /** This boat's contact capsule, as the collision sweep sizes it. */
  own: { halfSpine: number; radius: number };
  /** Closing speed below which meeting a hull is a free nudge; Infinity with collisions off. */
  nudgePx: number;
};

export type StepResult = {
  boat: BoatState;
  race: RaceState;
  /** Fraction of this move at which the race finished, or null. */
  finish: number | null;
  /**
   * This hull met one of `hulls` closing faster than a nudge, so the real sweep would charge it.
   * The landing ignores it: no shove is modelled.
   */
  touched: boolean;
};

const GATE_BOX_MARGIN = 20;

export function stepContext(
  map: MapDocument,
  feel: TurnFeel,
  raster: Raster,
  hull: HullDimensions = DEFAULT_HULL,
): StepContext {
  return {
    map,
    feel,
    raster,
    own: contactShape(hull),
    nudgePx: boatNudgeClosingPx(),
    gateBoxes: map.checkpoints.map((gate) => ({
      minX: Math.min(gate.a.x, gate.b.x) - GATE_BOX_MARGIN,
      minY: Math.min(gate.a.y, gate.b.y) - GATE_BOX_MARGIN,
      maxX: Math.max(gate.a.x, gate.b.x) + GATE_BOX_MARGIN,
      maxY: Math.max(gate.a.y, gate.b.y) + GATE_BOX_MARGIN,
    })),
  };
}

/** A boat at a pose, as the contact capsule the collision sweep would use, under way. */
export function hullObstacle(
  pose: { position: Vec2; heading: number; speed: number },
  dimensions: HullDimensions,
): FleetHull {
  const shape = contactShape(dimensions);
  const cos = Math.cos(pose.heading);
  const sin = Math.sin(pose.heading);
  const dx = cos * shape.halfSpine;
  const dy = sin * shape.halfSpine;

  return {
    from: { x: pose.position.x - dx, y: pose.position.y - dy },
    to: { x: pose.position.x + dx, y: pose.position.y + dy },
    radius: shape.radius,
    vx: cos * pose.speed,
    vy: sin * pose.speed,
  };
}

/** The parked hulls of a fleet, as contact capsules. */
export function fleetHulls(fleet: FleetContext | undefined): FleetHull[] {
  return fleet ? fleet.others.map((other) => hullObstacle(other.boat, other.dimensions)) : [];
}

/**
 * From the last `segmentGapSq`, so nothing is allocated per sample: the closest-point gap
 * `onFirst − onSecond`, and where on the second segment (0 at its start) the closest point is.
 */
const gap = { x: 0, y: 0, t: 0 };

/**
 * Meeting `hull` on its transom cap (the closest point is the spine's stern end), heading within
 * 60° of its way, with the push along its heading within 45°: running into its back, which the
 * rules never forgive. A diagonal brush past the stern corner is a side rub.
 */
function fromBehind(hull: FleetHull, cos: number, sin: number): boolean {
  if (gap.t > 1e-3) {
    return false;
  }

  const ax = hull.to.x - hull.from.x;
  const ay = hull.to.y - hull.from.y;
  const span = Math.hypot(ax, ay);
  // The push runs from this hull to that one: the opposite of the closest-point gap.
  const push = Math.hypot(gap.x, gap.y);

  return (
    span > 1e-6 &&
    (cos * ax + sin * ay) / span >= 0.5 &&
    (push < 1e-6 || (-gap.x * ax - gap.y * ay) / (push * span) >= Math.SQRT1_2)
  );
}

/** Squared distance between segments `p1→q1` and `p2→q2` (Ericson, Real-Time Collision Detection). */
function segmentGapSq(
  p1x: number,
  p1y: number,
  q1x: number,
  q1y: number,
  p2x: number,
  p2y: number,
  q2x: number,
  q2y: number,
): number {
  const d1x = q1x - p1x;
  const d1y = q1y - p1y;
  const d2x = q2x - p2x;
  const d2y = q2y - p2y;
  const rx = p1x - p2x;
  const ry = p1y - p2y;
  const a = d1x * d1x + d1y * d1y;
  const e = d2x * d2x + d2y * d2y;
  const f = d2x * rx + d2y * ry;
  let s = 0;
  let t = 0;

  if (a <= 1e-12 && e <= 1e-12) {
    gap.x = rx;
    gap.y = ry;
    gap.t = 0;
    return rx * rx + ry * ry;
  }

  if (a <= 1e-12) {
    t = Math.min(1, Math.max(0, f / e));
  } else {
    const c = d1x * rx + d1y * ry;

    if (e <= 1e-12) {
      s = Math.min(1, Math.max(0, -c / a));
    } else {
      const b = d1x * d2x + d1y * d2y;
      const denom = a * e - b * b;
      s = denom > 1e-12 ? Math.min(1, Math.max(0, (b * f - c * e) / denom)) : 0;
      t = (b * s + f) / e;

      if (t < 0) {
        t = 0;
        s = Math.min(1, Math.max(0, -c / a));
      } else if (t > 1) {
        t = 1;
        s = Math.min(1, Math.max(0, (b - c) / a));
      }
    }
  }

  gap.x = p1x + d1x * s - (p2x + d2x * t);
  gap.y = p1y + d1y * s - (p2y + d2y * t);
  gap.t = t;
  return gap.x * gap.x + gap.y * gap.y;
}

/** The same reach test `planFleetMove` runs before anything else. */
export function isLegalClick(map: MapDocument, feel: TurnFeel, boat: BoatState, dest: Vec2): boolean {
  const args = seatCommitArgs(boat, dest, feel);
  const wind = windOffsetPx(map.environment, boat.speed, args.windFeel);

  return isValidDestination(
    boat,
    dest,
    projectMovementArea(boat, args.radius, wind, args.coastPx),
    args.reach,
    args.bowOffsetPx,
  );
}

/**
 * The move a click makes, without the exact terrain run. Landing, heading, speed, and drive are
 * the same maths `planMove` uses on a clean move. Collision is the raster along the true arc,
 * every half cell; any land makes the click null, because the search only plans clean moves.
 * Meeting another boat's capsule faster than a nudge, or on its transom from behind, only sets
 * `touched`; the caller decides what that costs. A nudge-speed side brush is not modelled at
 * all, as the real sweep forgives it. Gates go through the real `advanceRace`.
 */
export function fastStep(
  ctx: StepContext,
  boat: BoatState,
  race: RaceState,
  dest: Vec2,
  hulls: readonly FleetHull[] = [],
): StepResult | null {
  const { feel, raster } = ctx;
  const args = seatCommitArgs(boat, dest, feel);
  const arc = solveTurnArc(boat, dest, args.turnTax);
  const landed = resolveMove(boat, dest, args.turnTax, args.keptSpeed);

  if (!arc) {
    return { boat: landed, race, finish: null, touched: false };
  }

  const x0 = boat.position.x;
  const y0 = boat.position.y;
  const h0 = boat.heading;
  const chord = Math.hypot(dest.x - x0, dest.y - y0);
  const straight = Math.abs(arc.offset) < 1e-9;
  const curvature = straight ? 0 : (2 * Math.sin(arc.offset)) / chord;
  const sin0 = Math.sin(h0);
  const cos0 = Math.cos(h0);
  const steps = Math.max(1, Math.ceil(arc.travelled / (raster.cell / 2)));
  // A boat that landed on the shoreline last turn sits in a solid raster cell; let it leave.
  let leaving = rasterSolidAt(raster, x0, y0);
  let minX = x0;
  let minY = y0;
  let maxX = x0;
  let maxY = y0;
  let touched = false;

  for (let i = 1; i <= steps; i += 1) {
    const s = (arc.travelled * i) / steps;
    let x: number;
    let y: number;

    if (straight) {
      x = x0 + cos0 * s;
      y = y0 + sin0 * s;
    } else {
      const h = h0 + curvature * s;
      x = x0 + (Math.sin(h) - sin0) / curvature;
      y = y0 - (Math.cos(h) - cos0) / curvature;
    }

    if (rasterSolidAt(raster, x, y)) {
      if (!leaving) {
        return null;
      }
    } else {
      leaving = false;
    }

    if (!touched && hulls.length > 0) {
      // This hull's own capsule on the arc's heading here, against each other capsule. Meeting
      // one only counts when it would cost a penalty: closing faster than a nudge, with each
      // boat at its way. Two boats at the same speed can sail side by side.
      const h = straight ? h0 : h0 + curvature * s;
      const cos = Math.cos(h);
      const sin = Math.sin(h);
      const hx = cos * ctx.own.halfSpine;
      const hy = sin * ctx.own.halfSpine;

      for (const hull of hulls) {
        const reach = ctx.own.radius + hull.radius;
        const gapSq = segmentGapSq(
          x - hx,
          y - hy,
          x + hx,
          y + hy,
          hull.from.x,
          hull.from.y,
          hull.to.x,
          hull.to.y,
        );

        if (gapSq >= reach * reach) {
          continue;
        }

        let nx = -gap.x;
        let ny = -gap.y;
        let norm = Math.sqrt(gapSq);

        if (norm < 1e-6) {
          nx = (hull.from.x + hull.to.x) / 2 - x;
          ny = (hull.from.y + hull.to.y) / 2 - y;
          norm = Math.hypot(nx, ny) || 1;
        }

        const closing =
          ((cos * arc.travelled - hull.vx) * nx + (sin * arc.travelled - hull.vy) * ny) / norm;

        // `nudgePx` is Infinity with collisions off: then no meeting counts, from behind or not.
        if (closing >= ctx.nudgePx || (Number.isFinite(ctx.nudgePx) && fromBehind(hull, cos, sin))) {
          touched = true;
          break;
        }
      }
    }

    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  if (leaving) {
    return null;
  }

  const nearGate = ctx.gateBoxes.some(
    (box) => box.minX <= maxX && box.maxX >= minX && box.minY <= maxY && box.maxY >= minY,
  );

  if (!nearGate || race.complete) {
    return { boat: landed, race, finish: null, touched };
  }

  const path = projectMovePath(boat, dest, 'arc', args.turnTax);
  const after = advanceRace(ctx.map, race, path);

  return {
    boat: landed,
    race: after,
    finish: after.complete ? (finishProgress(ctx.map, race, path) ?? 1) : null,
    touched,
  };
}
